import { Autocomplete } from '@mui/material';
import { makeStyles } from '@mui/styles';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import * as icons from 'apollo-react-icons';
import EllipsisHorizontal from 'apollo-react-icons/EllipsisHorizontal';
import Pencil from 'apollo-react-icons/Pencil';
import Plus from 'apollo-react-icons/Plus';
import Undo from 'apollo-react-icons/Undo';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import IconsModal from './components/IconsModal';
import { PropertyComponentModal } from './PropertyComponentModal/PropertyComponentModal';
import { getEnvVar } from '../../../../src/utils/env';
import { handleErrors } from '../../../actions/ErrorActions';
import { getSavedSearchesByEntity } from '../../../actions/SavedSearchesActions';
import { clearWarnings, removeWarningByIdAndMessage } from '../../../actions/WarningActions';
import { getApiConfigurations as getConfigurationsApi } from '../../../api/external-api';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA } from '../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import {
  addApiConfigurations,
  deleteSubgridFromGrid,
  updateControl,
  updateFormModelByIdAndProp,
} from '../../actions/editor.actions';
import {
  AUTOCOMPLETE,
  CHART_DATA_PROP,
  CONDITIONAL_SELECT,
  CONDITIONAL_TEXT_FIELD,
  CONTAINER_CONTROLLERS,
  CONTROL_TYPE_COMPONENT,
  CONTROL_TYPES_WITH_SAVED_SEARCH_CONFIG,
  DATASOURCE_TYPE_PREDEFINED,
  ENABLED_CONTROLLERS,
  ICON_FIELD,
  LINK_TEXT_FIELD,
  MAPPING_TEXT_FIELD,
  MERGED_ZOOM_ENABLED_PROPS,
  NEED_SIGNATURE_PROP_HELPER_TEXT,
  PROPNAMES,
  QUERY_PARAM_FIELD,
  RADIO,
  SELECT,
  TEXT_FIELD,
  TEXT_VIEW,
  ZOOM_ENABLED_PROPS,
} from '../../constants/editor.constants';
import {
  ATTACHMENT_TYPES,
  BUTTON_TYPES,
  CONTAINER_CONTROLS_WITH_MAPPING,
  DATALOOKUP_TYPE_EXTERNAL_API,
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
  ZOOM_SOURCE_TYPE,
} from '../../constants/form-manager.constants';
import { getDataSourcesList, getToken, getTranslations } from '../../selectors/common.selectors';
import {
  getAddedElementFormModelById,
  getDeltaChangesByControl,
  getOriginalPropByModel,
  getSelectedDataSource,
  getSsModalTargetIdOptions,
  hasControlPropChanges,
} from '../../selectors/editor.selectors';
import {
  getEntityFromEntityDefinition,
  getPropByModel,
} from '../../selectors/form-manager.selectors';
import { getSubGridByGridId } from '../../selectors/ssGrid.selectors';
import {
  getDatasourceFields,
  getDataSourceObjectList,
  getDataSourceSelectValues,
  modifyConfigurations,
  modifyDataObjectValues,
} from '../../utils/editor.utils';
import {
  equalityFn,
  testExpression,
  testJSON,
  updateRulesScripts,
} from '../../utils/form-manager.utils';

const useStyles = makeStyles({
  root: {
    display: 'flex',

    '& .deltaChange div[class*="MuiSelect-select"], .deltaChange input[class*="MuiInputBase-input"]': {
      borderColor: 'blue',
    },

    '& .conditional.deltaChange div[class*="MuiSelect-select"]': {
      borderRadius: '6px 0 0 6px !important',
    },

    '& div[class*="MuiFormGroup-root"].deltaChange': {
      border: '1px solid blue',
    },

    '& button[class*="MuiButtonBase-root"].deltaChange': {
      marginTop: '40px',
    },

    '& div button[class*="MuiButtonBase-root"].deltaChange': {
      marginTop: '31px',
    },

    '& .ui-changes div[class*="MuiSelect-select"], .ui-changes input[class*="MuiInputBase-input"]': {
      borderColor: 'orange',
    },
    '& div[class*="MuiFormGroup-root"]': {
      padding: '7px',
    },
    '& div[class*="MuiFormGroup-root"].ui-changes': {
      border: '1px solid orange',
    },
    '& svg': {
      fontSize: '16px',
    },
    '& .iconsField': { '& input': { cursor: 'pointer', userSelect: 'none' } },
  },
  iconButton: {
    border: '1px solid #d9d9d9',
    marginTop: '42px',
    borderRadius: '0px 4px 4px 0px',
    borderLeft: 'none',
    width: '35px',
    height: '2.5rem',
    '& svg': {
      margin: '10px 8px',
    },
    '&:disabled': {
      borderColor: '#b6b5b5',
    },
    cursor: 'pointer',
  },
  radioGroupWrapper: {
    marginTop: 16,
    '& div[role=radiogroup]': {
      padding: '0 7px',
    },
  },
  radioLabelDisabled: {
    '& label': {
      color: '#b5b5b5',
    },
  },
});

const getIcon = (iconName = '') => {
  const Icon = icons[iconName];

  if (!Icon) {
    return;
  }

  return <Icon fontSize="small" />;
};

const adminAppUrl = getEnvVar('REACT_APP_ADMIN_APP_URL');

const PropertyComponent = ({
  label,
  name,
  type,
  values,
  controlId,
  source = [],
  defaultValueProp,
}) => {
  const classes = useStyles();
  const savedSearches = useSelector((state) => state.savedSearches);
  const originalPropValue = useFormSelector(getOriginalPropByModel, { id: controlId, prop: name });
  const propValueStore = useFormSelector(getPropByModel, { id: controlId, prop: name });
  const buttonTypeProp = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.BUTTON_TYPE,
  });
  const formChanges = useFormSelector(
    getDeltaChangesByControl,
    { id: controlId, name },
    equalityFn
  );
  const entityName = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.ENTITY_NAME,
  });
  const zoomSource = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.ZOOM_SOURCE,
  });
  const dataLookUpType = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.DATA_LOOK_UP_TYPE,
  });
  const controlType = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });
  const defaultLabel = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.DEFAULT_LABEL,
  });
  const sourceIdentifier = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.DATA_OBJECT,
  });
  const displayIcon = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.DISPLAY_ICON,
  });
  const showPostSummaryGenerationMessage = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.SHOW_POST_SUMMARY_GENERATION_MESSAGE,
  });
  const showOperationCompleteMessage = useFormSelector(getPropByModel, {
    id: controlId,
    prop: FORM_MODEL_ATTRIBUTES.SHOW_OPERATION_COMPLETED_MESSAGE,
  });

  const subgridId = useFormSelector(getSubGridByGridId, { id: controlId });
  const isNewElement = useFormSelector(getAddedElementFormModelById, { controlId });
  const selectOptionsNewController = (isNewElement && isNewElement.optionsSelect) || [];
  const token = useFormSelector(getToken);
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [dataObjectValues, setDataObjectValues] = useState([]);

  const currentEntity = useFormSelector(getEntityFromEntityDefinition, { entityName });

  const translations = useFormSelector(getTranslations);

  const { Entity } = translations;

  const getSelectValuesFromEntity = useMemo(
    () =>
      Object.keys(currentEntity?.columns ?? [])
        .filter((field) => !['WARNINGS', 'ERRORS'].includes(field))
        .reduce((acc, field) => {
          acc.push({
            value: field,
            label: `${Entity[currentEntity?.columns[field]?.properties.Caption]} (${field})`,
          });
          return acc;
        }, []),
    [Entity, currentEntity]
  );

  const dataSourceKey = ['dataSourceKey'].includes(name);

  const dataSourceList = useFormSelector(getDataSourcesList);
  const selectedDataSource = useFormSelector(getSelectedDataSource, {
    prop: FORM_MODEL_ATTRIBUTES.DATA_SOURCE_KEY,
  });

  const ssModalTargetIdOptions = useFormSelector(getSsModalTargetIdOptions);

  const [dataSourceEditAction, setDataSourceEditAction] = useState('new');

  const sourceType =
    controlType === SS_CONTROL_TYPE.AI_SIMILAR_RECORDS ? 'SMARTSOLVEDATA' : dataLookUpType;
  const isDataObject =
    CONTROL_TYPES_WITH_SAVED_SEARCH_CONFIG.includes(controlType) &&
    name === FORM_MODEL_ATTRIBUTES.DATA_OBJECT &&
    sourceType;

  const isSearchName =
    CONTROL_TYPES_WITH_SAVED_SEARCH_CONFIG.includes(controlType) && name === 'searchName';

  const hasDeltaChangesInStore = !!(formChanges && formChanges.length);
  const hasUIChanges = useFormSelector(hasControlPropChanges, {
    id: controlId,
    prop: name,
  });

  const baseValue =
    hasDeltaChangesInStore &&
    (formChanges[0]?.Properties.find((item) => _.lowerFirst(item.name) === name).baseValue ?? '');

  const propValueTemp =
    Array.isArray(propValueStore) && [CONDITIONAL_SELECT].includes(type)
      ? 'conditional'
      : String(propValueStore).toLowerCase() === 'true'
      ? true
      : String(propValueStore).toLowerCase() === 'false'
      ? false
      : _.isObject(propValueStore)
      ? JSON.stringify(propValueStore)
      : _.isString(propValueStore)
      ? propValueStore.replace(/eval\|\|/g, '')
      : propValueStore;

  const propValue =
    propValueTemp && _.isString(propValueTemp)
      ? updateRulesScripts(propValueTemp, true)
      : propValueTemp;

  const tempDefaultValueProp = useMemo(() => {
    if (
      name.toLowerCase().includes('defaultlabel') &&
      (propValue === undefined || propValue === '')
    ) {
      return defaultLabel || '';
    }

    return (propValue === undefined || propValue === '') && defaultValueProp !== undefined
      ? defaultValueProp
      : propValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  const [value, setValue] = useState(propValue);
  const helpLinkDomainPart = `${adminAppUrl}/Help/`;

  const hasDeltaChanges = useMemo(
    () => hasDeltaChangesInStore && JSON.stringify(baseValue) !== JSON.stringify(propValueStore),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(propValueStore), hasDeltaChangesInStore, JSON.stringify(baseValue)]
  );

  useEffect(() => {
    setValue(tempDefaultValueProp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  useEffect(() => {
    switch (name) {
      case FORM_MODEL_ATTRIBUTES.COMMAND_NAME:
        setValue(buttonTypeProp === BUTTON_TYPES.OTHER ? 'Evaluate' : defaultValueProp);
        return;

      case FORM_MODEL_ATTRIBUTES.CAUSES_VALIDATION: {
        const controlChanges = {
          causesValidation: false,
        };
        if (buttonTypeProp === BUTTON_TYPES.DELETE) {
          setValue(false);
          dispatch(updateControl({ id: controlId, controlChanges }));
        }
        return;
      }

      case FORM_MODEL_ATTRIBUTES.NEED_SIGNATURE: {
        const controlChanges = {
          needSignature: false,
        };
        if (buttonTypeProp === BUTTON_TYPES.DELETE) {
          setValue(false);
          dispatch(updateControl({ id: controlId, controlChanges }));
        }
        return;
      }
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonTypeProp]);

  useEffect(() => {
    if (sourceIdentifier && isSearchName && sourceType === 'SMARTSOLVEDATA') {
      dispatch(getSavedSearchesByEntity(sourceIdentifier));
    }
  }, [dispatch, isSearchName, sourceType, sourceIdentifier]);

  if (controlType === SS_CONTROL_TYPE.MODAL && name === FORM_MODEL_ATTRIBUTES.TARGET_ID) {
    values = ssModalTargetIdOptions;
  }

  if (type === SELECT && ['dataSourceKey'].includes(name)) {
    values = getDataSourceSelectValues(dataSourceList);
  }

  if (selectedDataSource && type === SELECT && ['dataValueField', 'dataTextField'].includes(name)) {
    values = getDatasourceFields(selectedDataSource);
  }

  if (isDataObject) {
    values = dataObjectValues;
  }

  if (isSearchName) {
    const filteredItems = [];
    const items = savedSearches[sourceIdentifier]?.searches;
    if (items) {
      for (const item of items) {
        filteredItems.push({ value: item.searchName, label: item.searchName });
      }
    }

    values = filteredItems;
  }

  useEffect(() => {
    const fetchDataObjectValues = async () => {
      if (isDataObject && sourceType === DATALOOKUP_TYPE_EXTERNAL_API) {
        const response = await getConfigurationsApi();

        if (!response.hasError) {
          dispatch(addApiConfigurations(modifyConfigurations(response.resource)));
          setDataObjectValues(modifyDataObjectValues(response.resource));
        } else {
          dispatch(handleErrors(response.errors, { isTopBanner: true }));
        }
      } else if (isDataObject && sourceType !== DATALOOKUP_TYPE_EXTERNAL_API) {
        const datasourceObjectList = await getDataSourceObjectList(sourceType);
        setDataObjectValues(datasourceObjectList);
      }
    };

    fetchDataObjectValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, isDataObject]);

  const dataSourceLinkBuilder = () => {
    let dataSourceLink = '';
    if (selectedDataSource && ['LISTDATA'].includes(selectedDataSource?.dataSourceTypeStr)) {
      dataSourceLink = `${getEnvVar(
        'REACT_APP_ADMIN_APP_URL'
      )}/LST_LISTS_EDITOR.ASPX?ListObjectName=${
        selectedDataSource?.dataSourceName
      }&Token=${token}&NavAction=Edit`;
    }
    return dataSourceLink;
  };

  const dataSourceLink = dataSourceKey ? dataSourceLinkBuilder() : '';

  const [openModal, setOpenModal] = useState(false);
  const [openIconsModal, setOpenIconsModal] = useState(false);

  const disableHandler = () => {
    if (controlType === SS_CONTROL_TYPE.ZOOM) {
      if (name === FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER) {
        return true;
      }

      if (zoomSource) {
        return (
          zoomSource === ZOOM_SOURCE_TYPE.ENTITY ||
          (zoomSource === ZOOM_SOURCE_TYPE.MERGED && !MERGED_ZOOM_ENABLED_PROPS.includes(name)) ||
          (zoomSource === ZOOM_SOURCE_TYPE.FORM && !ZOOM_ENABLED_PROPS.includes(name))
        );
      }
    } else if (
      controlType === SS_CONTROL_TYPE.FILE_INPUT &&
      name === FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER
    ) {
      return false;
    } else if (
      controlType === SS_CONTROL_TYPE.BUTTON_BATCH &&
      name === FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER
    ) {
      return false;
    } else if (
      controlType === SS_CONTROL_TYPE.BUTTON ||
      (controlType === SS_CONTROL_TYPE.FILE_VIEWER &&
        name !== FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER)
    ) {
      return false;
    } else if (
      [FORM_MODEL_ATTRIBUTES.NEED_SIGNATURE, FORM_MODEL_ATTRIBUTES.MEANING].includes(name) &&
      [BUTTON_TYPES.CANCEL, BUTTON_TYPES.OTHER, BUTTON_TYPES.DELETE, BUTTON_TYPES.COPY].includes(
        buttonTypeProp
      )
    ) {
      return true;
    } else if (name === FORM_MODEL_ATTRIBUTES.USE_EXPANDABLE_ROWS && !subgridId) {
      return true;
    } else if (ENABLED_CONTROLLERS.includes(name) && CONTAINER_CONTROLLERS.includes(controlType)) {
      return false;
    } else if (name === FORM_MODEL_ATTRIBUTES.DISPLAY_ICON_COLOR && isNilOrEmpty(displayIcon)) {
      return true;
    } else if (
      name === FORM_MODEL_ATTRIBUTES.POST_SUMMARY_GENERATION_MESSAGE &&
      !showPostSummaryGenerationMessage
    ) {
      return true;
    } else if (
      name === FORM_MODEL_ATTRIBUTES.OPERATION_COMPLETED_MESSAGE &&
      !showOperationCompleteMessage
    ) {
      return true;
    } else {
      return !PROPNAMES.includes(name);
    }
  };

  const hideHandler = () => {
    return (
      controlType !== SS_CONTROL_TYPE.BUTTON ||
      buttonTypeProp !== BUTTON_TYPES.DELETE ||
      (name !== FORM_MODEL_ATTRIBUTES.NEED_SIGNATURE &&
        name !== FORM_MODEL_ATTRIBUTES.CAUSES_VALIDATION)
    );
  };

  const conditionalSelect =
    ([CONDITIONAL_SELECT].includes(type) && (['conditional'].includes(value) || testJSON(value))) ||
    testExpression(propValueStore);

  const rulesController = [TEXT_FIELD].includes(type) && ['update'].includes(name);

  const allowModalEdition = PROPNAMES.includes(name);

  const formulaEditor = [CONDITIONAL_TEXT_FIELD].includes(type) && allowModalEdition;

  const mappingEditor = [MAPPING_TEXT_FIELD].includes(type) && allowModalEdition;

  const createMarkup = () => {
    return {
      __html: `<a  target='_blank' style='color: blue; font-size: .9rem'  href='${dataSourceLink}'> Edit List: ${propValue} </a> `,
    };
  };

  const updateFormModel = ({ target: { value } }, isDiscardButtonClick = false) => {
    let parsedValueTemp = isDiscardButtonClick ? baseValue : value;
    if (value === 'true' || value === true) {
      parsedValueTemp = true;
    } else if (value === 'false' || value === false) {
      parsedValueTemp = false;
    } else if (value && value === 'conditional') {
      setOpenModal(true);
      return;
    }

    if (
      formulaEditor &&
      mappingEditor &&
      (value === undefined ||
        // eslint-disable-next-line eqeqeq
        propValue == parsedValueTemp ||
        (propValue === undefined && value === ''))
    ) {
      return;
    }

    if (
      typeof propValueStore === 'string' &&
      propValueStore.startsWith('eval||') &&
      parsedValueTemp === propValue
    ) {
      return;
    }

    if (name === 'dataSourceKey') {
      changeDataSourceFieldsInModel({ dataSourceKey: parsedValueTemp });
    } else if (
      [SS_CONTROL_TYPE.ZOOM, SS_CONTROL_TYPE.SAVED_SEARCH].includes(controlType) &&
      name === FORM_MODEL_ATTRIBUTES.DATA_LOOK_UP_TYPE
    ) {
      handleLookUpTypeChanges(parsedValueTemp);
      dispatch(removeWarningByIdAndMessage(controlId, 'Source Type is required!'));
    } else if (controlType === SS_CONTROL_TYPE.GRID && name === FORM_MODEL_ATTRIBUTES.GRID_TYPE) {
      handleGridTypeChange(parsedValueTemp);
    } else {
      if (
        [SS_CONTROL_TYPE.ZOOM, SS_CONTROL_TYPE.SAVED_SEARCH].includes(controlType) &&
        name === FORM_MODEL_ATTRIBUTES.DATA_OBJECT
      ) {
        dispatch(removeWarningByIdAndMessage(controlId, 'Source Identifier is required!'));
      }

      if (
        controlType === SS_CONTROL_TYPE.SAVED_SEARCH &&
        name === FORM_MODEL_ATTRIBUTES.SEARCH_NAME
      ) {
        dispatch(removeWarningByIdAndMessage(controlId, 'Search Name is required!'));
      }

      const hasControlPropChanged =
        parsedValueTemp !== '' ? propValueStore !== parsedValueTemp : !_.isNil(propValueStore);

      //reset value to original (undefined) before first save if we clear the field
      parsedValueTemp =
        _.isNil(originalPropValue) &&
        (parsedValueTemp === '' || parsedValueTemp === defaultValueProp)
          ? originalPropValue
          : parsedValueTemp;

      if (hasControlPropChanged) {
        if (
          (name === FORM_MODEL_ATTRIBUTES.FILTER ||
            name === FORM_MODEL_ATTRIBUTES.DATA_SOURCE_FILTER ||
            name === FORM_MODEL_ATTRIBUTES.SOURCE_FIELDS) &&
          !isDiscardButtonClick
        ) {
          return;
        }

        dispatch(updateFormModelByIdAndProp(controlId, name, parsedValueTemp));
      }
    }

    if (
      controlType === SS_CONTROL_TYPE.SAVED_SEARCH &&
      name === FORM_MODEL_ATTRIBUTES.DATA_LOOK_UP_TYPE
    ) {
      dispatch(updateFormModelByIdAndProp(controlId, FORM_MODEL_ATTRIBUTES.SEARCH_NAME, ''));
    }

    if (controlType === SS_CONTROL_TYPE.FILE_VIEWER && parsedValueTemp === true) {
      if (name === FORM_MODEL_ATTRIBUTES.IS_DOCUMENT_CONTENT) {
        dispatch(
          updateFormModelByIdAndProp(controlId, FORM_MODEL_ATTRIBUTES.IS_PORTAL_CONTENT, '')
        );
      } else if (name === FORM_MODEL_ATTRIBUTES.IS_PORTAL_CONTENT) {
        dispatch(
          updateFormModelByIdAndProp(controlId, FORM_MODEL_ATTRIBUTES.IS_DOCUMENT_CONTENT, '')
        );
      }
    }
  };

  const changeDataSourceFieldsInModel = ({ dataSourceKey, needPersistUserAction }) => {
    const controlChanges = {
      dataSourceKey: dataSourceKey ?? '',
      dataValueField: '',
      dataTextField: '',
      dataSourceFilter: '',
    };
    dispatch(updateControl({ id: controlId, controlChanges, needPersistUserAction }));
  };

  const handleLookUpTypeChanges = (dataLookUpType) => {
    const controlChanges = {
      dataLookUpType,
      dataObject: '',
    };

    dispatch(updateControl({ id: controlId, controlChanges }));
  };

  const handleGridTypeChange = (gridTypeValue) => {
    dispatch(updateFormModelByIdAndProp(controlId, name, gridTypeValue));
    if (ATTACHMENT_TYPES.includes(gridTypeValue)) {
      dispatch(deleteSubgridFromGrid(controlId));
    }
  };

  const changeConditionalTextFieldValue = ({ target: { value } }) => {
    if (
      name === FORM_MODEL_ATTRIBUTES.FILTER ||
      name === FORM_MODEL_ATTRIBUTES.DATA_SOURCE_FILTER ||
      name === FORM_MODEL_ATTRIBUTES.SOURCE_FIELDS
    ) {
      return;
    }
    setValue(value);
  };

  const canDeselectValue =
    controlType !== SS_CONTROL_TYPE.ZOOM || (name !== 'dataLookUpType' && name !== 'dataObject');

  const shouldDisplayViewOnlyButton =
    disableHandler() &&
    (name === FORM_MODEL_ATTRIBUTES.FILTER || name === FORM_MODEL_ATTRIBUTES.DATA_MAPPING_LIST) &&
    value;

  const shouldDisplayAddDataSourceButton =
    dataSourceKey && !CONTAINER_CONTROLS_WITH_MAPPING.includes(controlType);

  const displayedConditionalText =
    value &&
    (name === FORM_MODEL_ATTRIBUTES.FILTER || name === FORM_MODEL_ATTRIBUTES.DATA_SOURCE_FILTER)
      ? 'Condition...'
      : value?.length && name === FORM_MODEL_ATTRIBUTES.SOURCE_FIELDS
      ? 'Fields...'
      : value?.length && name === FORM_MODEL_ATTRIBUTES.EXTRA_PROMPTS
      ? 'Extra Prompts...'
      : value ?? '';

  const discardButton = () => (
    <Tooltip className="deltaChange" title={translateString('Rollback to Pilgrim value')}>
      <IconButton onClick={(event) => updateFormModel(event, true)}>
        <Undo color="primary" />
      </IconButton>
    </Tooltip>
  );

  const confirmIconSelect = (iconName) => {
    dispatch(updateFormModelByIdAndProp(controlId, name, iconName));

    const clearWarningsFilter = ({ instanceId }) => instanceId !== controlId;

    batch(() => {
      dispatch(updateFormModelByIdAndProp(controlId, name, iconName));
      dispatch(clearWarnings(clearWarningsFilter));
    });
  };

  switch (type) {
    case AUTOCOMPLETE: {
      const renderSource = [...source, { label: propValue }];

      return (
        <Autocomplete
          label={label}
          source={renderSource}
          fullWidth
          value={[value]}
          defaultValue={[propValue]}
          singleSelect
          disabled={disableHandler()}
          ui-ta={getUITA('CtrlProp', name)}
        />
      );
    }
    case CONDITIONAL_SELECT:
      return (
        <div className={classes.root}>
          <Select
            label={label}
            fullWidth
            style={conditionalSelect ? { width: '95%' } : {}}
            className={{
              deltaChange: !isNewElement && hasDeltaChanges,
              conditional: !isNewElement && conditionalSelect,
              'ui-changes': !isNewElement && hasUIChanges,
            }}
            value={testJSON(value) || testExpression(propValueStore) ? 'conditional' : value}
            onChange={updateFormModel}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
          >
            {values &&
              values.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
          </Select>
          {conditionalSelect && (
            <IconButton
              className={classes.iconButton}
              onClick={() => setOpenModal(true)}
              ui-ta={getUITA('Edit', name, 'condition')}
            >
              <EllipsisHorizontal />
            </IconButton>
          )}
          {allowModalEdition && openModal && (
            <PropertyComponentModal
              open={openModal}
              label={label}
              onClose={() => setOpenModal(false)}
              controlId={controlId}
              name={name}
              setActualValue={setValue}
            ></PropertyComponentModal>
          )}
          {hasDeltaChanges && discardButton()}
        </div>
      );

    case SELECT:
      return (
        <>
          <div className={classes.root}>
            <Select
              className={{
                deltaChange: !isNewElement && hasDeltaChanges,
                conditional: !isNewElement && conditionalSelect,
                'ui-changes': !isNewElement && hasUIChanges,
              }}
              fullWidth
              label={label}
              value={value ?? ''}
              onChange={updateFormModel}
              disabled={disableHandler()}
              canDeselect={canDeselectValue}
              ui-ta={getUITA('CtrlProp', name)}
            >
              {(values || getSelectValuesFromEntity).map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {allowModalEdition && openModal && (
              <PropertyComponentModal
                open={openModal}
                label={label}
                onClose={() => setOpenModal(false)}
                controlId={controlId}
                name={name}
                dataSourceEditAction={dataSourceEditAction}
                setDataSourceEditAction={setDataSourceEditAction}
                changeDataSourceFieldsInModel={changeDataSourceFieldsInModel}
              ></PropertyComponentModal>
            )}
            {shouldDisplayAddDataSourceButton && (
              <>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    setDataSourceEditAction('new');
                    setOpenModal(true);
                  }}
                  ui-ta={getUITA('AddDatasource')}
                >
                  <Plus />
                </IconButton>
                {value && (
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => {
                      setDataSourceEditAction('edit');
                      setOpenModal(true);
                    }}
                    disabled={selectedDataSource?.dataSourceType === DATASOURCE_TYPE_PREDEFINED}
                    ui-ta={getUITA('EditDatasource')}
                  >
                    <Pencil />
                  </IconButton>
                )}
              </>
            )}

            {hasDeltaChanges && discardButton()}
          </div>
          {dataSourceLink ? <div dangerouslySetInnerHTML={createMarkup()} /> : ' '}
        </>
      );

    case RADIO:
      return (
        hideHandler() && (
          <>
            <div className={classes.root}>
              <div
                className={classNames(
                  classes.radioGroupWrapper,
                  disableHandler() ? classes.radioLabelDisabled : ''
                )}
              >
                <RadioGroup
                  className={{
                    'ui-changes': !isNewElement && hasUIChanges,
                    deltaChange: !isNewElement && hasDeltaChanges,
                  }}
                  label={label}
                  aria-label="label"
                  name="label"
                  value={value ?? defaultValueProp}
                  row
                  onChange={(event) => updateFormModel(event, false)}
                  disabled={disableHandler()}
                  ui-ta={getUITA('CtrlProp', name)}
                >
                  {values &&
                    values.map(({ value, label }) => (
                      <Radio key={value} value={value} label={label} />
                    ))}
                </RadioGroup>
              </div>
              <div>{hasDeltaChanges && discardButton()}</div>
            </div>
            {name === FORM_MODEL_ATTRIBUTES.NEED_SIGNATURE ? (
              <div style={{ fontSize: '.9rem' }}>
                {translateString(NEED_SIGNATURE_PROP_HELPER_TEXT)}
              </div>
            ) : null}
          </>
        )
      );

    case CONDITIONAL_TEXT_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            fullWidth
            className={{
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            value={displayedConditionalText}
            icon={<EllipsisHorizontal />}
            iconProps={{ onClick: () => setOpenModal(true) }}
            onBlur={updateFormModel}
            onChange={changeConditionalTextFieldValue}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
            inputProps={{
              readOnly: name === FORM_MODEL_ATTRIBUTES.SOURCE_FIELDS,
            }}
          />
          {shouldDisplayViewOnlyButton && (
            <IconButton
              className={classes.iconButton}
              onClick={() => {
                setOpenModal(true);
              }}
            >
              <EllipsisHorizontal />
            </IconButton>
          )}
          {allowModalEdition && openModal && (
            <PropertyComponentModal
              open={openModal}
              label={label}
              onClose={() => setOpenModal(false)}
              controlId={controlId}
              name={name}
              isReadOnly={shouldDisplayViewOnlyButton}
            ></PropertyComponentModal>
          )}
          {hasDeltaChanges && discardButton()}
        </div>
      );
    case MAPPING_TEXT_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            fullWidth
            className={{
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            value={propValueStore?.length ? 'Mapping...' : ''}
            icon={!shouldDisplayViewOnlyButton && <EllipsisHorizontal />}
            iconProps={{ onClick: () => setOpenModal(true) }}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
          />
          {shouldDisplayViewOnlyButton && (
            <IconButton
              className={classes.iconButton}
              onClick={() => {
                setOpenModal(true);
              }}
            >
              <EllipsisHorizontal />
            </IconButton>
          )}
          {allowModalEdition && openModal && (
            <PropertyComponentModal
              open={openModal}
              label={label}
              onClose={() => setOpenModal(false)}
              controlId={controlId}
              name={name}
              isReadOnly={shouldDisplayViewOnlyButton}
            ></PropertyComponentModal>
          )}
          {hasDeltaChanges && discardButton()}
        </div>
      );

    case QUERY_PARAM_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            fullWidth
            className={{
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            value={value ? 'Query Params...' : ''}
            icon={<EllipsisHorizontal />}
            iconProps={{ onClick: () => setOpenModal(true) }}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
          />
          {allowModalEdition && openModal && (
            <PropertyComponentModal
              open={openModal}
              label={label}
              onClose={() => setOpenModal(false)}
              controlId={controlId}
              name={name}
              isReadOnly={shouldDisplayViewOnlyButton}
            ></PropertyComponentModal>
          )}
          {hasDeltaChanges && discardButton()}
        </div>
      );

    case CONTROL_TYPE_COMPONENT:
      return (
        <div className={classes.root}>
          <Select
            placeholder="Select a Control Type"
            fullWidth
            label={label}
            value={value}
            canDeselect={false}
            onChange={
              selectOptionsNewController && selectOptionsNewController.length > 1
                ? updateFormModel
                : undefined
            }
            ui-ta={getUITA('CtrlProp', name)}
          >
            {selectOptionsNewController.map(({ icon, text, value }) => {
              return (
                <MenuItem key={value} value={value}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ paddingRight: 4, paddingTop: 4 }}>{icon}</div>
                    <div>{text}</div>
                  </div>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      );

    case TEXT_VIEW:
      return (
        <>
          <div style={{ display: 'flex', marginBottom: '.3rem' }}>
            <div>
              <label>{label}</label>
              <div>{value || '-'}</div>
            </div>
          </div>
        </>
      );

    case LINK_TEXT_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            startIcon={<div style={{ color: '#808080' }}>{'Help/'}</div>}
            fullWidth
            className={{
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            placeholder="CAPAManHelp/CAPAM.htm"
            helperText={
              <p
                style={{ overflowWrap: 'anywhere' }}
              >{`${helpLinkDomainPart}${displayedConditionalText}`}</p>
            }
            inputProps={{ style: { paddingLeft: 50 } }}
            value={displayedConditionalText}
            onBlur={updateFormModel}
            onChange={changeConditionalTextFieldValue}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
          />
        </div>
      );

    case ICON_FIELD:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            fullWidth
            inputProps={{ readOnly: true, tabIndex: -1 }}
            className={{
              iconsField: true,
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            aria-invalid
            value={value || 'Select an icon'}
            icon={<EllipsisHorizontal />}
            iconProps={{ onClick: () => setOpenIconsModal(true) }}
            startIcon={value ? getIcon(value) : null}
            disabled={disableHandler()}
            ui-ta={getUITA('CtrlProp', name)}
            onClick={() => setOpenIconsModal(true)}
          />
          {openIconsModal && (
            <IconsModal
              handleClose={() => setOpenIconsModal(false)}
              confirmIconSelect={confirmIconSelect}
              showNoneItem={name === 'displayIcon'}
            />
          )}
        </div>
      );

    case CHART_DATA_PROP:
      return (
        <div className={classes.root}>
          <TextField
            label={label}
            fullWidth
            className={{
              'ui-changes': !isNewElement && hasUIChanges,
              deltaChange: !isNewElement && hasDeltaChanges,
            }}
            value={propValueStore?.length ? 'Data...' : ''}
            icon={<EllipsisHorizontal />}
            iconProps={{ onClick: () => setOpenModal(true) }}
            ui-ta={getUITA('CtrlProp', name)}
          />
          {allowModalEdition && openModal && (
            <PropertyComponentModal
              open={openModal}
              label={label}
              onClose={() => setOpenModal(false)}
              controlId={controlId}
              name={name}
            ></PropertyComponentModal>
          )}
          {hasDeltaChanges && discardButton()}
        </div>
      );

    case TEXT_FIELD:
    default:
      return (
        <>
          <div className={classes.root}>
            <TextField
              label={label}
              fullWidth
              className={{
                'ui-changes': !isNewElement && hasUIChanges,
                deltaChange: !isNewElement && hasDeltaChanges,
              }}
              style={rulesController ? { width: '90%' } : {}}
              value={value ?? ''}
              onBlur={updateFormModel}
              onChange={({ target: { value } }) => setValue(value)}
              disabled={disableHandler()}
              ui-ta={getUITA('CtrlProp', name)}
            />
            {rulesController && openModal && (
              <>
                <div style={{ marginTop: '3rem', marginLeft: '1rem' }}>
                  <EllipsisHorizontal onClick={() => setOpenModal(true)} />
                </div>
                <PropertyComponentModal
                  open={openModal}
                  label={label}
                  onClose={() => setOpenModal(false)}
                  controlId={controlId}
                  name={name}
                ></PropertyComponentModal>
              </>
            )}
            {hasDeltaChanges && discardButton()}
          </div>
        </>
      );
  }
};

export default PropertyComponent;
